/**
 * SEARCH-002 — `SearchPaletteBody` : états + colonne d'aperçu + bande basse.
 *
 * Extrait mécaniquement de `SearchPalette.tsx` (aucun changement de logique) :
 * états (vide 2 colonnes / résultats 55-45 / aucun résultat), colonne d'aperçu et
 * bande basse. Partagé par les 2 shells (desktop `SearchGlobal`, mobile
 * `GlobalSearchDialog`).
 */

import { useTranslation } from 'react-i18next'
import { ArrowRight, SearchX, Star } from 'lucide-react'
import { CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { CATEGORY_REGISTRY } from '@/lib/searchCategories'
import { SearchPreviewPanel } from './SearchPreviewPanel'
import { type SearchPaletteApi } from './useSearchPalette'
import { CREATE_SHORTCUT_LABEL, DISPLAY_CAP } from './searchPaletteHelpers'
import { CenteredAction, ContextualCreateBand, HelpPill, HitItem, TyposSkippedNote } from './SearchPaletteBodyParts'
import { SearchPaletteEmptyState } from './SearchPaletteEmptyState'

// ===========================================================================
// SearchPaletteBody — états + colonne d'aperçu + bande basse.
// ===========================================================================
export function SearchPaletteBody({
    palette,
    variant,
}: {
    palette: SearchPaletteApi
    variant: 'desktop' | 'mobile'
}) {
    const { t } = useTranslation()
    const {
        input,
        go,
        rememberQuery,
        hits,
        groups,
        bestHit,
        data,
        isLoading,
        isError,
        isTimeout,
        typosSkipped,
        refetch,
        enabled,
        showEmptyState,
        showTooShort,
        showNoResults,
        showResults,
        hasPreviewColumn,
        openHit,
        setMobilePreview,
        setSelectedValue,
        desktopPreviewQuery,
        previewTarget,
        openQuickLook,
        entityId,
    } = palette
    const isDesktop = variant === 'desktop'

    return (
        <>
            {/* aria-live */}
            <div className="sr-only" aria-live="polite">
                {showResults ? t('globalSearch.aria.count', { count: hits.length }) : ''}
                {showNoResults ? t('globalSearch.state.no_results', { q: input }) : ''}
                {(showResults || showNoResults) && typosSkipped ? (
                    <span> {t('globalSearch.state.typos_skipped')}</span>
                ) : null}
            </div>

            <div className={isDesktop ? 'flex min-h-0 flex-1' : 'flex min-h-0 flex-1 flex-col'}>
                <CommandList
                    className={
                        isDesktop
                            ? 'min-w-0 max-h-[min(58vh,460px)] flex-1'
                            : 'min-w-0 flex-1 max-h-none'
                    }
                >
                    {/* ── ÉTAT VIDE : 2 colonnes (Aller à + Créer | Filtrer par + Récents) ── */}
                    {showEmptyState && <SearchPaletteEmptyState palette={palette} />}

                    {showTooShort && (
                        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                            {t('globalSearch.state.too_short')}
                        </div>
                    )}

                    {enabled && isLoading && !data && (
                        <div className="space-y-2 p-3" aria-hidden="true">
                            {[0, 1, 2, 3].map((i) => (
                                <div key={i} className="h-9 rounded bg-muted animate-pulse motion-reduce:animate-none" />
                            ))}
                        </div>
                    )}

                    {isError && (
                        <div className="flex flex-col items-center gap-2 px-4 py-6 text-center text-sm">
                            <span className="text-muted-foreground">
                                {t(isTimeout ? 'globalSearch.state.timeout' : 'globalSearch.state.error')}
                            </span>
                            <button
                                type="button"
                                onClick={() => refetch()}
                                className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-accent"
                            >
                                {t('globalSearch.state.retry')}
                            </button>
                        </div>
                    )}

                    {/* MEILLEUR RÉSULTAT — seulement sur la requête COURANTE (pas de
                        fantôme à champ vidé via keepPreviousData). */}
                    {showResults && bestHit && (
                        <CommandGroup
                            heading={
                                <span className="flex items-center gap-1.5">
                                    <Star className="h-3 w-3" aria-hidden="true" />
                                    {t('globalSearch.group.best')}
                                </span>
                            }
                        >
                            <HitItem
                                hit={bestHit}
                                query={input}
                                leading
                                onOpen={openHit}
                                onMobilePreview={setMobilePreview}
                                onHover={setSelectedValue}
                            />
                        </CommandGroup>
                    )}

                    {/* GROUPES */}
                    {showResults &&
                        groups.map(({ type, hits: groupHits }) => {
                            const cfg = CATEGORY_REGISTRY[type]
                            const visible = groupHits.slice(0, DISPLAY_CAP)
                            const truncated = groupHits.length > DISPLAY_CAP
                            const Icon = cfg.icon
                            return (
                                <CommandGroup
                                    key={type}
                                    heading={
                                        <span className="sticky top-0 flex items-center gap-1.5 bg-popover">
                                            <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                                            {t(`globalSearch.group.${cfg.i18nKey}`)}
                                        </span>
                                    }
                                >
                                    {visible.map((hit) => (
                                        <HitItem
                                            key={`${hit.type}:${hit.id}`}
                                            hit={hit}
                                            query={input}
                                            onOpen={openHit}
                                            onMobilePreview={setMobilePreview}
                                            onHover={setSelectedValue}
                                        />
                                    ))}
                                    {truncated && cfg.listing && (
                                        <CommandItem
                                            value={`overflow:${type}`}
                                            onSelect={() => {
                                                rememberQuery()
                                                go(cfg.listing!.toRoute(input, groupHits[0]?.matchedField ?? null))
                                            }}
                                            className="text-xs text-muted-foreground"
                                        >
                                            <ArrowRight aria-hidden="true" />
                                            {t('globalSearch.overflow', { module: t(`globalSearch.group.${cfg.i18nKey}`) })}
                                        </CommandItem>
                                    )}
                                </CommandGroup>
                            )
                        })}

                    {/* Correction des fautes abandonnée faute de temps (`typos_skipped`) : la
                        liste ne porte que les fiches trouvées SANS faute, il en manque peut-être. */}
                    {showResults && typosSkipped && <TyposSkippedNote />}

                    {/* AUCUN RÉSULTAT — bloc unifié, centré : icône + message + conseil
                        + actions GROUPÉES dessous (création primaire / assistant / aide).
                        Plus de bande basse séparée (évite l'effet « flottant/coupé »). */}
                    {showNoResults && (
                        <div className="flex min-h-[260px] flex-col items-center justify-center px-6 py-10 text-center">
                            <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                                <SearchX className="h-7 w-7 text-primary" aria-hidden="true" />
                            </span>
                            <div className="text-base font-medium text-foreground">
                                {t('globalSearch.state.no_results', { q: input })}
                            </div>
                            <div className="mt-1.5 max-w-sm text-sm text-muted-foreground">
                                {t('globalSearch.state.no_results_advice')}
                            </div>
                            {typosSkipped && <TyposSkippedNote className="mt-3 max-w-sm" />}
                            {/* Propositions NAVIGABLES au clavier : ← ↑ ↓ → déplacent la
                                bague (`data-nav` + `nextSpatialValue`), Entrée valide l'action
                                surlignée (cf. `noResultActions` + `handleCommandKeyDown`). Le
                                survol souris pose aussi la sélection. Noir = création non
                                ambiguë ; violet = assistant ; contour = aide. */}
                            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                                {palette.noResultActions.map((a) => (
                                    <CenteredAction
                                        key={a.value}
                                        icon={a.icon}
                                        variant={a.variant}
                                        dataNav={a.value}
                                        selected={palette.noResultSel === a.value}
                                        onClick={a.run}
                                        onHover={() => palette.setNoResultSel(a.value)}
                                    >
                                        {a.kind === 'create' && a.create
                                            ? t(`globalSearch.create.typed.${a.create.i18nKey}`, { q: input.trim() })
                                            : t('globalSearch.bridge.search_help', { q: input.trim() })}
                                    </CenteredAction>
                                ))}
                            </div>
                        </div>
                    )}
                </CommandList>

                {/* Colonne d'aperçu — desktop uniquement, ~45 % (résultats 55 %). */}
                {isDesktop && hasPreviewColumn && (
                    // Borne + scroll interne : sans ça, un aperçu haut (vignette PDF +
                    // boutons) déborde SOUS la bande basse (chevauchement Télécharger /
                    // « Rechercher dans l'aide »). Même hauteur max que la liste.
                    <div className="w-[44%] shrink-0 overflow-y-auto border-l border-border max-h-[min(58vh,460px)]">
                        <SearchPreviewPanel
                            data={desktopPreviewQuery.data}
                            isLoading={desktopPreviewQuery.isLoading}
                            isError={desktopPreviewQuery.isError}
                            onRetry={() => void desktopPreviewQuery.refetch()}
                            currentEntityId={entityId}
                            onQuickLook={openQuickLook}
                            onSendEmail={palette.openComposeEmail}
                            onNavigate={palette.go}
                            onOpen={() => {
                                if (!previewTarget) return
                                const hit = hits.find(
                                    (h) => h.type === previewTarget.type && h.id === previewTarget.id,
                                )
                                if (hit) openHit(hit)
                            }}
                        />
                    </div>
                )}
            </div>

            {/* ── Bande basse (1 ligne, 1 trait fin, SANS fond ni séparateur vertical) ──
                Code couleur : création TEAL à gauche · aide VIOLET à droite (§1).
                État vide  → aide unique violet, centrée dans la 2ᵉ colonne (1ʳᵉ vide).
                Résultats  → « Créer « q » » + puces compactes (⌘↵ sur la 1ʳᵉ) | aide unique.
                Aucun résultat → pas de bande ici (actions dans le bloc centré). */}
            {showEmptyState ? (
                // `pb` safe-area : sur mobile cette bande touche le bas de l'écran plein écran.
                <div className="grid grid-cols-1 border-t border-border p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] sm:grid-cols-2">
                    <div className="hidden sm:block" />
                    <HelpPill label={t('globalSearch.help.ask')} onClick={palette.handleBrowseHelp} className="w-full" />
                </div>
            ) : showResults ? (
                // Mobile : colonne (créations en haut, aide pleine largeur dessous) + safe-area
                // basse. Desktop (≥ sm) : 1 ligne, création à gauche / aide à droite.
                <div className="flex flex-col gap-2 border-t border-border px-3 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-2 sm:pb-2">
                    <ContextualCreateBand
                        options={palette.createOptions}
                        query={input}
                        onGo={go}
                        primaryShortcut={CREATE_SHORTCUT_LABEL}
                    />
                    <HelpPill
                        label={t('globalSearch.bridge.search_help', { q: input.trim() })}
                        onClick={palette.handleHelp}
                        className="w-full sm:w-auto"
                    />
                </div>
            ) : null}
        </>
    )
}
